import { useMemo, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "../components/Card";
import { Button } from "../components/Button";
import { Link } from "react-router-dom";

export default function Galerie({ photos = [] }) {
    const [filterYear, setFilterYear] = useState("ALL");
    const [filterProject, setFilterProject] = useState("ALL");
    const [sortMode, setSortMode] = useState("YEAR_ASC");
    const [visibleCount, setVisibleCount] = useState(36);

    const [selectedIndex, setSelectedIndex] = useState(null);

    const years = useMemo(() => {
        return Array.from(new Set(photos.map((p) => p.year))).sort((a, b) => a - b);
    }, [photos]);

    const projects = useMemo(() => {
        return Array.from(new Set(photos.map((p) => p.project))).sort((a, b) =>
            a.localeCompare(b)
        );
    }, [photos]);

    const filteredSortedPhotos = useMemo(() => {
        let arr = [...photos];

        if (filterYear !== "ALL") arr = arr.filter((p) => p.year === Number(filterYear));
        if (filterProject !== "ALL") arr = arr.filter((p) => p.project === filterProject);

        arr.sort((a, b) => {
            const yearCmp = (a.year ?? 0) - (b.year ?? 0);
            const orderCmp = (a.order ?? 0) - (b.order ?? 0);

            if (sortMode === "YEAR_ASC") return yearCmp !== 0 ? yearCmp : orderCmp;
            return yearCmp !== 0 ? -yearCmp : orderCmp;
        });

        return arr;
    }, [photos, filterYear, filterProject, sortMode]);

    const shown = useMemo(
        () => filteredSortedPhotos.slice(0, visibleCount),
        [filteredSortedPhotos, visibleCount]
    );

    const canLoadMore = shown.length < filteredSortedPhotos.length;

    const onChangeYear = (v) => {
        setFilterYear(v);
        setVisibleCount(36);
    };

    const onChangeProject = (v) => {
        setFilterProject(v);
        setVisibleCount(36);
    };

    const closeLightbox = useCallback(() => {
        setSelectedIndex(null);
    }, []);

    const nextImage = useCallback(() => {
        setSelectedIndex((prev) =>
            prev < filteredSortedPhotos.length - 1 ? prev + 1 : prev
        );
    }, [filteredSortedPhotos.length]);

    const prevImage = useCallback(() => {
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    }, []);

    // Keyboard controls + scroll lock
    useEffect(() => {
        if (selectedIndex !== null) {
            document.body.style.overflow = "hidden";

            const handleKey = (e) => {
                if (e.key === "Escape") closeLightbox();
                if (e.key === "ArrowRight") nextImage();
                if (e.key === "ArrowLeft") prevImage();
            };

            window.addEventListener("keydown", handleKey);
            return () => {
                window.removeEventListener("keydown", handleKey);
                document.body.style.overflow = "auto";
            };
        }
    }, [selectedIndex, closeLightbox, nextImage, prevImage]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-blue-50 to-amber-50 p-6 space-y-8">
            <div className="max-w-6xl mx-auto space-y-3 text-center">
                <h1 className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-amber-500">
                    Galerie
                </h1>
                <p className="text-gray-700">
                    Explorează fotografiile pe ani și proiecte.
                </p>
            </div>

            <div className="max-w-6xl mx-auto">
                <Card className="bg-white/80 backdrop-blur">
                    <CardContent className="p-4 md:p-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                        <div className="flex flex-wrap gap-2 items-center">
                            <label className="text-sm text-gray-700">An:</label>
                            <select
                                value={filterYear}
                                onChange={(e) => onChangeYear(e.target.value)}
                                className="border rounded-xl px-3 py-2 bg-white"
                            >
                                <option value="ALL">Toți anii</option>
                                {years.map((y) => (
                                    <option key={y} value={String(y)}>
                                        {y}
                                    </option>
                                ))}
                            </select>

                            <label className="text-sm text-gray-700 ml-2">Proiect:</label>
                            <select
                                value={filterProject}
                                onChange={(e) => onChangeProject(e.target.value)}
                                className="border rounded-xl px-3 py-2 bg-white"
                            >
                                <option value="ALL">Toate activitățile</option>
                                {projects.map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="max-w-6xl mx-auto">
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {shown.map((img, idx) => {
                        const realIndex = filteredSortedPhotos.findIndex(
                            (p) => p.src === img.src
                        );

                        return (
                            <motion.div
                                key={img.id ?? `${img.src}-${idx}`}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.3 }}
                                className="rounded-2xl overflow-hidden shadow bg-white/80 cursor-pointer hover:scale-105 transition"
                                onClick={() => setSelectedIndex(realIndex)}
                            >
                                <img
                                    src={img.src}
                                    alt={img.alt}
                                    className="w-full h-44 object-cover"
                                    loading="lazy"
                                />
                                <div className="p-3 space-y-1">
                                    <div className="text-sm font-semibold">{img.alt}</div>
                                    <div className="text-xs text-gray-600">
                                        {img.year} • {img.project}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {canLoadMore && (
                        <div className="flex justify-center mt-6">
                            <Button onClick={() => setVisibleCount((c) => c + 36)}>
                                Încarcă mai multe
                            </Button>
                        </div>
                )}

                <Link to="/">
                    <Button variant="outline" className="w-full mt-6">
                        Înapoi la pagina principală
                    </Button>
                </Link>
            </div>

            {/* LIGHTBOX */}
            <AnimatePresence>
                {selectedIndex !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-6"
                        onClick={closeLightbox}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="relative max-w-6xl w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={filteredSortedPhotos[selectedIndex].src}
                                alt={filteredSortedPhotos[selectedIndex].alt}
                                className="w-full max-h-[85vh] object-contain rounded-xl"
                            />

                            {/* Close */}
                            <button
                                onClick={closeLightbox}
                                className="absolute top-4 right-4 text-white text-2xl"
                            >
                                ✕
                            </button>

                            {/* Prev */}
                            {selectedIndex > 0 && (
                                <button
                                    onClick={prevImage}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-3xl"
                                >
                                    ‹
                                </button>
                            )}

                            {/* Next */}
                            {selectedIndex < filteredSortedPhotos.length - 1 && (
                                <button
                                    onClick={nextImage}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-3xl"
                                >
                                    ›
                                </button>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}